/**
 * NotificationsBell — the header bell: unread badge + popover inbox.
 *
 * Reads the `notifications` Redux slice (hydrated/streamed by RealtimeProvider +
 * useNotificationsHook). Lets the user filter All/Unread, mark rows read (click a
 * row → mark read + jump to the loan), mark all read, load older rows, and open
 * the preferences dialog. Self-contained so TopHeader just drops it in.
 *
 * Opening the bell counts as reading: everything unread is marked read right
 * away so the badge clears. The rows that *were* unread keep their highlight
 * until the popover closes (`justReadIds`), so opening it doesn't wipe out the
 * only cue for what's new.
 */
import { useCallback, useMemo, useState } from "react";

import DoneAllOutlined from "@mui/icons-material/DoneAllOutlined";
import NotificationsNoneOutlined from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { useNotificationsHook } from "../../hooks/useNotificationsHook";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { notificationMarkRead } from "../../redux/reducers/notificationsReducer";
import NotificationPreferencesDialog from "./NotificationPreferencesDialog";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { resolveNotificationLink } from "./notificationTarget";

// Severity → accent color (dot + unread rail).
const SEVERITY_COLOR = {
  high: "#dc2626",
  med: "#f59e0b",
  low: "#64748b",
  info: "#1F5AF2",
};

// "loan_changed" → "Loan changed"
const humanizeType = (type = "") =>
  type
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

const timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso, "", "M/d/yyyy");
};

// Strip HTML so the bell body is a clean single line (server body may be html).
const toText = (html) =>
  typeof html === "string" ? html.replace(/<[^>]+>/g, " ").trim() : "";

const REALTIME_HINT = {
  unconfigured: "Live updates not yet enabled — checking periodically.",
  disconnected: "Reconnecting to live updates…",
  connecting: "Connecting to live updates…",
};

const NotificationRow = ({ n, unread, expanded, onToggle, onClick }) => {
  const link = resolveNotificationLink(n);
  const color = SEVERITY_COLOR[n.severity] || SEVERITY_COLOR.info;
  const body = toText(n.body);
  // Show a "Show more/less" control when the title is likely truncated or the
  // body would overflow the 2-line clamp (~88 chars at the popover width).
  const expandable = (n.title && n.title.length > 38) || body.length > 90;

  return (
    <Box
      onClick={() => onClick(n)}
      sx={{
        px: 2,
        py: 1.25,
        cursor: "pointer",
        borderLeft: `3px solid ${unread ? color : "transparent"}`,
        background: unread ? "rgba(31,90,242,0.04)" : "transparent",
        "&:hover": { background: "rgba(15,23,42,0.05)" },
      }}
    >
      <Flx ac jb g={1}>
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              flexShrink: 0,
              alignSelf: expanded ? "flex-start" : "center",
              mt: expanded ? "5px" : 0,
              background: unread ? color : "#cbd5e1",
            }}
          />
          <Txt
            component={expanded ? "div" : "span"}
            sx={{
              fontSize: 12.5,
              fontWeight: unread ? 700 : 500,
              color: "#0f172a",
              ...(expanded
                ? { whiteSpace: "normal", overflowWrap: "anywhere" }
                : {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
            }}
          >
            {n.title || humanizeType(n.type)}
          </Txt>
        </Flx>
        <Txt sx={{ fontSize: 10.5, color: "text.secondary", flexShrink: 0 }}>
          {timeAgo(n.createdAt)}
        </Txt>
      </Flx>
      {body && (
        <Txt
          component="div"
          sx={{
            mt: 0.25,
            ml: "15px",
            fontSize: 11.5,
            color: "text.secondary",
            overflowWrap: "anywhere",
            ...(expanded
              ? { whiteSpace: "pre-line" }
              : {
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }),
          }}
        >
          {body}
        </Txt>
      )}
      <Flx ac jb g={1} sx={{ mt: 0.4, ml: "15px" }}>
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 9.5,
              fontWeight: 600,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              color,
              background: `${color}14`,
              borderRadius: "4px",
              px: 0.6,
              py: "1px",
              flexShrink: 0,
            }}
          >
            {humanizeType(n.type)}
          </Box>
          {n.actor?.name || n.actor?.email ? (
            <Txt
              sx={{
                fontSize: 10.5,
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {n.actor.name || n.actor.email}
            </Txt>
          ) : null}
        </Flx>
        {expandable && (
          <Txt
            onClick={(e) => {
              e.stopPropagation();
              onToggle(n);
            }}
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              color: "#1F5AF2",
              flexShrink: 0,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {expanded ? "Show less" : "Show more"}
          </Txt>
        )}
      </Flx>
      {expanded && link && (
        <Box sx={{ ml: "15px", mt: 0.75 }}>
          <Txt
            onClick={(e) => {
              e.stopPropagation();
              onClick(n, { forceNavigate: true });
            }}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "#1F5AF2",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {link.label} →
          </Txt>
        </Box>
      )}
    </Box>
  );
};

const NotificationsBell = ({ iconColor = "inherit" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAdmin } = usePermissionsHook();
  const [anchor, setAnchor] = useState(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Rows that were unread when the popover opened. They're read on the server
  // already, but stay highlighted until it closes so the user can see what's new.
  const [justReadIds, setJustReadIds] = useState(() => new Set());

  const { items, unread, loaded, hasMore, realtime } = useSelector(
    (s) => s.notifications,
  );
  const { markRead, markAllRead, loadMore, hydrateInbox } =
    useNotificationsHook();

  const open = Boolean(anchor);

  // Viewing the inbox counts as reading it.
  const handleOpen = useCallback(
    async (e) => {
      setAnchor(e.currentTarget);
      const seen = new Set(items.filter((n) => !n.readAt).map((n) => n._id));
      setJustReadIds(seen);
      // Refresh on open so the list is current even if a poll just lapsed, and
      // so anything that arrived since is included in the mark-read below.
      const fresh = await hydrateInbox();
      if (Array.isArray(fresh)) {
        fresh.forEach((n) => {
          if (!n.readAt) seen.add(n._id);
        });
        setJustReadIds(new Set(seen));
      }
      if (seen.size > 0) markAllRead();
    },
    [items, hydrateInbox, markAllRead],
  );

  const handleClose = useCallback(() => {
    setAnchor(null);
    setJustReadIds(new Set());
  }, []);

  // Unread for display purposes — a row read by this open stays highlighted.
  const isUnread = useCallback(
    (n) => !n.readAt || justReadIds.has(n._id),
    [justReadIds],
  );

  const visible = useMemo(
    () => (showUnreadOnly ? items.filter(isUnread) : items),
    [items, showUnreadOnly, isUnread],
  );

  const newCount = useMemo(
    () => items.filter(isUnread).length,
    [items, isUnread],
  );

  const markReadOnce = useCallback(
    (n) => {
      if (!n.readAt) {
        dispatch(notificationMarkRead(n._id));
        markRead(n._id);
      }
    },
    [dispatch, markRead],
  );

  // Toggle the inline "Show more / Show less" expansion (and mark read on expand).
  const handleToggleExpand = useCallback(
    (n) => {
      markReadOnce(n);
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(n._id)) next.delete(n._id);
        else next.add(n._id);
        return next;
      });
    },
    [markReadOnce],
  );

  const handleRowClick = useCallback(
    (n, opts = {}) => {
      markReadOnce(n);
      // Has a destination (loan, submissions lane, data.url path) → navigate.
      // Nothing to open → expand inline so the full text is readable.
      const link = resolveNotificationLink(n, { isAdmin });
      if (link) {
        handleClose();
        navigate(link.to);
      } else if (!opts.forceNavigate) {
        handleToggleExpand(n);
      }
    },
    [markReadOnce, navigate, handleToggleExpand, handleClose, isAdmin],
  );

  const handleLoadMore = useCallback(async () => {
    const oldest = items[items.length - 1];
    if (!oldest) return;
    setLoadingMore(true);
    await loadMore(oldest._id);
    setLoadingMore(false);
  }, [items, loadMore]);

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton onClick={handleOpen} size="small" sx={{ color: iconColor }}>
          <Badge
            badgeContent={unread}
            color="error"
            max={99}
            invisible={unread === 0}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "9px",
                height: 15,
                minWidth: 15,
                px: "4px",
              },
            }}
          >
            <NotificationsNoneOutlined />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 380, mt: 1, overflow: "hidden" } } }}
      >
        {/* Header */}
        <Flx
          ac
          jb
          sx={{ px: 2, py: 1.25, borderBottom: "1px solid #f0f0f0" }}
        >
          <Flx ac g={1}>
            <Txt sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              Notifications
            </Txt>
            {unread > 0 && (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#fff",
                  background: "#dc2626",
                  borderRadius: 999,
                  px: 0.7,
                  py: "1px",
                }}
              >
                {unread}
              </Box>
            )}
          </Flx>
          <Flx ac g={0.5}>
            <Tooltip title="Mark all read" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={markAllRead}
                  disabled={unread === 0}
                >
                  <DoneAllOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Notification settings" arrow>
              <IconButton size="small" onClick={() => setPrefsOpen(true)}>
                <SettingsOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Flx>
        </Flx>

        {/* Filter toggle */}
        <Flx g={0.5} sx={{ px: 1.5, py: 0.75, borderBottom: "1px solid #f5f5f5" }}>
          {[
            { key: false, label: "All" },
            // Counts the session's just-read rows too, so the label matches what
            // the filter actually shows after the open-marks-read pass.
            { key: true, label: `Unread${newCount ? ` (${newCount})` : ""}` },
          ].map((opt) => (
            <Button
              key={String(opt.key)}
              size="small"
              variant={showUnreadOnly === opt.key ? "contained" : "text"}
              onClick={() => setShowUnreadOnly(opt.key)}
              sx={{
                minWidth: 0,
                px: 1.25,
                py: 0.25,
                fontSize: 11.5,
                textTransform: "none",
              }}
            >
              {opt.label}
            </Button>
          ))}
        </Flx>

        {/* Realtime status hint */}
        {REALTIME_HINT[realtime] && (
          <Box
            sx={{
              px: 2,
              py: 0.5,
              background: "#fffbeb",
              borderBottom: "1px solid #fef3c7",
            }}
          >
            <Txt sx={{ fontSize: 10.5, color: "#92400e" }}>
              {REALTIME_HINT[realtime]}
            </Txt>
          </Box>
        )}

        {/* List */}
        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {!loaded ? (
            <Flx center sx={{ py: 5 }}>
              <CircularProgress size={22} />
            </Flx>
          ) : visible.length === 0 ? (
            <Flx column center sx={{ py: 5, px: 2, textAlign: "center" }}>
              <NotificationsNoneOutlined
                sx={{ fontSize: 28, color: "text.disabled", mb: 1 }}
              />
              <Txt sx={{ fontSize: 12, color: "text.secondary" }}>
                {showUnreadOnly
                  ? "No unread notifications"
                  : "You're all caught up"}
              </Txt>
            </Flx>
          ) : (
            visible.map((n, i) => (
              <Box key={n._id}>
                {i > 0 && <Divider sx={{ borderColor: "#f5f5f5" }} />}
                <NotificationRow
                  n={n}
                  unread={isUnread(n)}
                  expanded={expandedIds.has(n._id)}
                  onToggle={handleToggleExpand}
                  onClick={handleRowClick}
                />
              </Box>
            ))
          )}

          {loaded && !showUnreadOnly && hasMore && visible.length > 0 && (
            <Flx center sx={{ py: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{ fontSize: 11.5, textTransform: "none" }}
              >
                {loadingMore ? "Loading…" : "Load older"}
              </Button>
            </Flx>
          )}
        </Box>

        {/* Footer — jump to the full-page inbox */}
        <Box sx={{ borderTop: "1px solid #f0f0f0" }}>
          <Button
            fullWidth
            variant="text"
            onClick={() => {
              handleClose();
              navigate("/notifications");
            }}
            sx={{
              py: 1,
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 0,
            }}
          >
            See all notifications
          </Button>
        </Box>
      </Popover>

      <NotificationPreferencesDialog
        open={prefsOpen}
        onClose={() => setPrefsOpen(false)}
      />
    </>
  );
};

export default NotificationsBell;
